import { queryOptions } from '@tanstack/react-query';
import * as friendRecApi from '../endpoints/friend-recommendation';

/**
 * Query keys for friend recommendations
 */
export const FRIEND_RECOMMENDATION_QUERY_KEYS = {
  all: ['friend-recommendations'] as const,
  received: () => [...FRIEND_RECOMMENDATION_QUERY_KEYS.all, 'received'] as const,
  sent: () => [...FRIEND_RECOMMENDATION_QUERY_KEYS.all, 'sent'] as const,
  menuItem: (menuItemId: string) => [...FRIEND_RECOMMENDATION_QUERY_KEYS.all, 'menu-item', menuItemId] as const,
  sentMenuItem: (menuItemId: string) => [...FRIEND_RECOMMENDATION_QUERY_KEYS.all, 'sent-menu-item', menuItemId] as const,
} as const;

/**
 * Query options for received friend recommendations (paginated)
 *
 * @param params - Query parameters for pagination
 * @returns Query options for received recommendations
 */
export const receivedFriendRecommendationsQueryOptions = (params: { page?: number; per_page?: number } = {}) =>
  queryOptions({
    queryKey: [...FRIEND_RECOMMENDATION_QUERY_KEYS.received(), params] as const,
    queryFn: () => friendRecApi.getReceivedFriendRecommendations(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for recommendations related to a specific menu item
 *
 * @param menuItemId - The menu item ID to fetch recommendations for
 * @returns Query options for menu item recommendations
 */
export const menuItemFriendRecommendationsQueryOptions = (menuItemId: string) =>
  queryOptions({
    queryKey: FRIEND_RECOMMENDATION_QUERY_KEYS.menuItem(menuItemId),
    queryFn: () => friendRecApi.getMenuItemFriendRecommendations(menuItemId),
    enabled: !!menuItemId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });

/**
 * Query options for recommendations the current user has sent for a specific menu item
 *
 * @param menuItemId - The menu item ID to fetch sent recommendations for
 * @returns Query options for sent menu item recommendations
 */
export const sentMenuItemRecommendationsQueryOptions = (menuItemId: string) =>
  queryOptions({
    queryKey: FRIEND_RECOMMENDATION_QUERY_KEYS.sentMenuItem(menuItemId),
    queryFn: () => friendRecApi.getSentMenuItemRecommendations(menuItemId),
    enabled: !!menuItemId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    meta: {
      persist: false, // No persisted cache: auth-required data
    },
  });
